import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cx } from "../cx";

export type ButtonVariant = "primary" | "secondary" | "danger-quiet";
/** sm 36 px (row actions), md 42 px (page actions), lg 46 px (panel actions), xl 50 px (decisions). */
export type ButtonSize = "sm" | "md" | "lg" | "xl";

interface Common {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Grow to fill the row (the main action next to a secondary one). */
  block?: boolean;
  children: ReactNode;
}

export type ButtonProps = Common & ButtonHTMLAttributes<HTMLButtonElement>;

/** Pill button: primary black with white text, secondary tile grey with black text, danger-quiet tile grey with red text. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", block, className, type = "button", children, ...rest },
  ref,
) {
  return (
    <button ref={ref} type={type} className={cx("lcc-btn", `lcc-btn--${variant}`, `lcc-btn--${size}`, block && "lcc-btn--block", className)} {...rest}>
      {children}
    </button>
  );
});

export type LinkButtonProps = Common & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

/** A link that looks like a pill button (navigation, e.g. "Review" into a module). */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(function LinkButton(
  { variant = "primary", size = "md", block, className, children, ...rest },
  ref,
) {
  return (
    <a ref={ref} className={cx("lcc-btn", `lcc-btn--${variant}`, `lcc-btn--${size}`, block && "lcc-btn--block", className)} {...rest}>
      {children}
    </a>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  /** Required: an icon-only button needs words for screen readers. */
  label: string;
  icon: ReactNode;
  /** `round` grey 42 px (top bar, panel close); `map` white 44 px with a shadow (map controls). */
  shape?: "round" | "map";
  size?: "sm" | "md";
  /** A small red dot (e.g. new notifications); say the count in `label`. */
  badge?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, shape = "round", size = "md", badge, className, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx("lcc-iconbtn", `lcc-iconbtn--${shape}`, size === "sm" && "lcc-iconbtn--sm", className)}
      {...rest}
    >
      {icon}
      {badge && <span className="lcc-iconbtn__badge" aria-hidden="true" />}
    </button>
  );
});
